import React from 'react';
import { ResizablePanels } from '../ui/ResizablePanels';
import { ChatHistory } from '../chat/ChatHistory';
import { ChatInput } from '../chat/ChatInput';
import { AnalysisReport } from '../dashboard/AnalysisReport';
import { AgentLoadingSequence } from '../ui/AgentLoadingSequence';
import { classNames } from '../../utils/classNames';
import { useDemo } from '../../contexts/DemoContext';
import { Button } from '../ui/Button';

interface PolicyAnalysisWorkflowProps {
  session: any; // Replace with proper session type
  sendMessage: (message: string) => void;
  loadDemoReport?: (topic: string, question: string) => void;
  onCancelAnalysis?: () => void;
}

export const PolicyAnalysisWorkflow: React.FC<PolicyAnalysisWorkflowProps> = ({
  session,
  sendMessage,
  loadDemoReport,
  onCancelAnalysis,
}) => {
  const [chatVisible, setChatVisible] = React.useState(false);
  const [isCanceling, setIsCanceling] = React.useState(false);
  const { isDemoMode, sampleQuestions } = useDemo();

  // Create demo welcome message for display
  const demoWelcomeMessage = React.useMemo(() => ({
    role: 'assistant' as const,
    content: `**Demo Mode**

Politheon AI analyst is ready to provide instant federal legislative analysis. Select a sample topic below for immediate comprehensive reports with visualizations and key insights.`,
    timestamp: new Date().toISOString(),
  }), []);

  // Get messages to display (include demo welcome if needed)
  const displayMessages = React.useMemo(() => {
    if (isDemoMode && session.messages.length === 0 && !session.isLoading) {
      return [demoWelcomeMessage, ...session.messages];
    }
    return session.messages;
  }, [isDemoMode, session.messages, session.isLoading, demoWelcomeMessage]);

  const handleCancel = () => {
    if (isCanceling) return;
    setIsCanceling(true);
    // Call the cancel function immediately for visual feedback
    onCancelAnalysis?.();
  };

  // Reset cancel state when loading stops
  React.useEffect(() => {
    if (!session.isLoading) {
      setIsCanceling(false);
    }
  }, [session.isLoading]);

  const handleSampleQuestionClick = (question: string) => {
    if (isDemoMode && loadDemoReport) {
      // In demo mode, try to load cached report for instant results
      const sample = sampleQuestions.find(q => q.question === question);
      const topic = sample?.id || 'healthcare';
      
      console.log('🎯 Demo mode: Loading cached report for', topic);
      loadDemoReport(topic, question);
    } else {
      // Regular mode - use normal message sending
      sendMessage(question);
    }
  };

  const SampleQuestions = () => (
    <div className="space-y-4 max-w-2xl">
      <div className="text-center mb-6">
        <h4 className="text-lg font-semibold theme-text-primary mb-2">
          Try These Sample Questions
        </h4>
        <p className="theme-text-secondary text-sm">
          Explore our demo with these curated policy analysis examples
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {sampleQuestions.map((sample, index) => {
          const getFrameClasses = () => {
            switch(sample.id) {
              case 'healthcare':
                return 'shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 border-emerald-400/30 hover:border-emerald-400/60';
              case 'ai':
                return 'shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 border-blue-400/30 hover:border-blue-400/60';
              case 'environmental':
                return 'shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 border-purple-400/30 hover:border-purple-400/60';
              case 'travel':
                return 'shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 border-orange-400/30 hover:border-orange-400/60';
              default:
                return 'shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 border-emerald-400/30 hover:border-emerald-400/60';
            }
          };

          return (
            <Button
              key={sample.id}
              variant="secondary"
              onClick={() => handleSampleQuestionClick(sample.question)}
              className={`p-4 h-auto text-left theme-bg-sidebar-hover theme-border-sidebar hover:theme-bg-tertiary transition-all duration-300 group relative w-full hover:scale-105 hover:-translate-y-1 backdrop-blur-sm border-2 ${getFrameClasses()} min-h-[180px] flex`}
              disabled={session.isLoading}
            >
              <div className="flex items-start space-x-3 w-full">
                <div className="text-2xl flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-200">
                  {sample.icon}
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-between">
                  <div>
                    <div className="font-semibold theme-text-primary text-base mb-2">
                      {sample.topic}
                    </div>
                    <div className="theme-text-secondary text-sm leading-relaxed line-clamp-5">
                      {sample.question}
                    </div>
                  </div>
                  <div className="theme-text-secondary text-xs opacity-80 mt-2">
                    {sample.description}
                  </div>
                </div>
              </div>
            </Button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Desktop Layout (lg and above) */}
      <div className="flex-1 min-h-0 hidden lg:block">
        <ResizablePanels
        initialLeftPercent={20}
        minLeftPercent={15}
        maxLeftPercent={40}
        left={(
          <div className="h-full theme-bg-chat backdrop-blur-sm border-r theme-border flex flex-col">
            <ChatHistory messages={displayMessages} isLoading={session.isLoading} />
            <ChatInput onSendMessage={sendMessage} isLoading={session.isLoading} />
          </div>
        )}
        right={(
          <div className="h-full theme-bg-analysis backdrop-blur-sm flex flex-col">
            {session.analysisComplete ? (
              <div className="overflow-y-auto flex-1 p-6">
                <AnalysisReport session={session} />
              </div>
            ) : (
              <div className="h-full p-6">
                {isDemoMode ? (
                  <div className="pt-8 flex justify-center">
                    <SampleQuestions />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4 border border-yellow-500/30">
                        <svg className="w-8 h-8 theme-accent-yellow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold theme-text-primary mb-2">
                        Ready for Analysis
                      </h3>
                      <p className="theme-text-secondary max-w-sm">
                        Ask a question about policy or legislation to generate a comprehensive analysis report.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
              />
      </div>

      {/* Tablet Layout (md to lg) */}
      <div className="flex-1 min-h-0 hidden md:block lg:hidden">
        <ResizablePanels
        initialLeftPercent={15}
        minLeftPercent={12}
        maxLeftPercent={30}
        left={(
          <div className="h-full theme-bg-chat backdrop-blur-sm border-r theme-border flex flex-col">
            <ChatHistory messages={displayMessages} isLoading={session.isLoading} />
            <ChatInput onSendMessage={sendMessage} isLoading={session.isLoading} />
          </div>
        )}
        right={(
          <div className="h-full theme-bg-analysis backdrop-blur-sm flex flex-col">
            {session.analysisComplete ? (
              <div className="overflow-y-auto flex-1 p-4">
                <AnalysisReport session={session} />
              </div>
            ) : (
              <div className="h-full p-4">
                {isDemoMode ? (
                  <div className="pt-6 flex justify-center">
                    <SampleQuestions />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="w-14 h-14 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4 border border-yellow-500/30">
                        <svg className="w-7 h-7 theme-accent-yellow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <h3 className="text-base font-semibold theme-text-primary mb-2">
                        Ready for Analysis
                      </h3>
                      <p className="theme-text-secondary max-w-sm text-sm">
                        Ask a question about policy or legislation to generate a comprehensive analysis report.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
              />
      </div>

      {/* Mobile Chat and Analysis (below md) */}
      <div className="md:hidden flex-1 min-h-0 flex flex-col relative">
        {/* Chat Section - Collapsible when analysis is present */}
        {session.analysisComplete ? (
          <>
            {/* Collapsible Chat Panel */}
            <div className={classNames(
              "theme-bg-chat backdrop-blur-sm flex flex-col transition-all duration-300 ease-in-out",
              chatVisible ? "h-1/5" : "h-0 overflow-hidden"
            )}>
              <ChatHistory messages={displayMessages} isLoading={session.isLoading} />
              <ChatInput onSendMessage={sendMessage} isLoading={session.isLoading} />
            </div>
            
            {/* Analysis Panel - Flexes to fill available space */}
            <div className={classNames(
              "theme-bg-analysis backdrop-blur-sm overflow-hidden transition-all duration-300 ease-in-out",
              chatVisible ? "flex-1" : "flex-1"
            )}>
              <div className="h-full flex flex-col">
                <div className="text-center p-3 border-b border-slate-600/30 bg-slate-800/20">
                  <h3 className="text-sm font-medium theme-text-primary uppercase tracking-wide">
                    Analysis Report
                  </h3>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
              <AnalysisReport session={session} />
            </div>
          </div>
        </div>
            
            {/* Floating Chat Bubble */}
            <button
              onClick={() => setChatVisible(!chatVisible)}
              className="fixed bottom-4 right-4 w-14 h-14 bg-blue-600 hover:bg-blue-700 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 z-50"
            >
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </button>
          </>
        ) : (
          <>
            {/* Chat Section - Collapsible when no analysis */}
            <div className={classNames(
              "theme-bg-chat backdrop-blur-sm flex flex-col transition-all duration-300 ease-in-out",
              chatVisible ? "h-1/2 md:h-1/6" : "h-0 overflow-hidden"
            )}>
              <ChatHistory messages={displayMessages} isLoading={session.isLoading} />
              <ChatInput onSendMessage={sendMessage} isLoading={session.isLoading} />
            </div>
            
            {/* Ready for Analysis Panel - Flexes to fill available space */}
            <div className={classNames(
              "theme-bg-analysis backdrop-blur-sm overflow-hidden transition-all duration-300 ease-in-out",
              chatVisible ? "flex-1" : "flex-1"
            )}>
              <div className="h-full flex flex-col">
                <div className="text-center p-3 border-b border-slate-600/30 bg-slate-800/20">
                  <h3 className="text-sm font-medium theme-text-primary uppercase tracking-wide">
                    Ready for Analysis
                  </h3>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                  {isDemoMode ? (
                    <div className="pt-4">
                      <SampleQuestions />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4 border border-yellow-500/30">
                          <svg className="w-8 h-8 theme-accent-yellow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold theme-text-primary mb-2">
                          Ready for Analysis
                        </h3>
                        <p className="theme-text-secondary max-w-sm">
                          Ask a question about policy or legislation to generate a comprehensive analysis report.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Floating Chat Bubble */}
            <button
              onClick={() => setChatVisible(!chatVisible)}
              className="fixed bottom-4 right-4 w-14 h-14 bg-blue-600 hover:bg-blue-700 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 z-50"
            >
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Agent Loading Sequence */}
      <AgentLoadingSequence 
        isVisible={session.isLoading}
        totalDurationMs={10000}
        onComplete={() => {
          // Loading sequence complete - the actual API response will handle state updates
        }}
        onCancel={handleCancel}
        isCanceling={isCanceling}
      />
    </div>
  );
};